<template>
    <div class="main-search">
        <v-select :placeholder="searchPlaceholder" :filterable="false" :options="options" :value="area" @search="onSearch"
            @input="onInput">
            <template v-slot:option="option">
                {{ option.label }}
            </template>
            <template v-slot:selected-option="option">
                {{ option.label_formatted }}
            </template>
            <template v-slot:no-options="{ search, searching }">
                <template v-if="searching && search.length > 2">
                    No results found for&nbsp;<em>{{ search }}</em>.
                </template>
                <template v-else>
                    <em style="opacity: 0.5">
                        Start typing to search for an area.
                    </em>
                </template>
            </template>
        </v-select>
        <button class="btn btn--secondary" type="button" v-if="hasSearchButton" @click.prevent="redirectTo(redirectUrl)"
            :disabled="isLoading">
            Search
        </button>
    </div>
</template>

<script>
import { slugFromString } from '@helpers/helpers';
import { getSearchUrlSegment1, redirectTo } from '@helpers/url-handler';
import resizeMixin from '@mixins/resizeMixin';
import { getGoogleMapsAPI } from 'gmap-vue';
import _ from 'lodash';
import vSelect from 'vue-select';
import { mapMutations, mapState } from 'vuex';

export default {
    mixins: [resizeMixin],
    components: {
        vSelect,
    },
    props: {
        hasSearchButton: {
            type: Boolean,
            required: false,
        },
        isRentSelected: {
            type: Boolean,
            required: true,
        },
        propertyType: {
            type: String,
            default: null,
        },
        scrollFocusOnSearch: {
            type: Boolean,
            required: false,
            default: false,
        },
        activeTabIndex: {
            type: Number,
            required: false,
            default: 0,
        },
    },
    data() {
        return {
            options: [],
            sessionToken: null,
            isLoading: false,
        };
    },
    computed: {
        ...mapState('area', ['area']),
        google: getGoogleMapsAPI,
        searchPlaceholder() {
            if (this.area) {
                return '';
            }
            if (this.isScreenLowerThanDesktop) {
                return 'e.g. ‘London’, ‘NW1’';
            }
            return 'e.g. ‘London’, ‘Camden’ or ‘NW1’';
        },
        redirectUrl() {
            const urlSegment1 = getSearchUrlSegment1(this.isRentSelected);
            let redirectUrl = `/${urlSegment1}`;

            if (this.area && this.area.slug) {
                redirectUrl += `/${this.area.slug}`;
            }
            return redirectUrl;
        },
    },
    async created() {
        await this.$gmapApiPromiseLazy();
    },
    mounted() {
        this.setOptions(this.area);
    },
    methods: {
        ...mapMutations('area', [
            'setArea',
        ]),

        redirectTo,
        onSearch: _.debounce(async function (input, loading) {
            if (this.scrollFocusOnSearch) {
                const areaSelect = document.querySelector('.vs__dropdown-menu');
                areaSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }

            if (input.length < 2) {
                return;
            }
            loading(true);
            this.options = await this.runAutocomplete(input);
            loading(false);
        }, 500),

        runAutocomplete(input) {
            this.sessionToken = new this.google.maps.places.AutocompleteSessionToken();
            const service = new this.google.maps.places.AutocompleteService();
            const options = {
                sessionToken: this.sessionToken,
                language: 'en',
                input,
                componentRestrictions: {
                    country: ['gb'],
                },
                types: ['geocode'],
            };
            return service.getPlacePredictions(options)
                .then(({ predictions }) => {
                    if (!predictions) {
                        return [];
                    }
                    return predictions.map((prediction) => ({
                        label: prediction.description,
                        place_id: prediction.place_id,
                        slug: slugFromString(prediction.description),
                    }));
                });
        },

        setOptions(area) {
            this.options = area ? [area] : [];
        },

        async onInput(event) {
            let area = null;

            if (event && event.place_id) {
                const { place_id, label } = event;
                this.isLoading = true;
                area = await this.saveArea(place_id, label);
                this.isLoading = false;

                if (window.appInProduction) {
                    window.gtag('event', 'search_place', {
                        place: label,
                    });
                }
            }

            await this.setArea(area);
            this.$emit('input', area);

            // this.redirectTo(this.redirectUrl);
        },

        saveArea(placeId, label) {
            return this.axios.post(`/api/area/place/${placeId}/${this.sessionToken[Object.keys(this.sessionToken)[0]]}`, { label })
                .then((res) => res.data)
                .then(({ data }) => data);
        },
    },
};
</script>
